import { CornerDownLeftIcon, SquareIcon } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import MessageQueue from "./MessageQueue";
import { chatStoreInstance } from "./stores";

import ContentArea, { ContentAreaRef, ContentNodeType } from "@/components/ContentArea";
import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (message: string) => void;
  onStop?: () => void;
  isLoading: boolean;
  placeholder?: string;
  className?: string;
  hasMessages?: boolean;
}

export interface ChatInputRef {
  focus: () => void;
  getValue: () => string;
  setValue: (value: string) => void;
}

export const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(
  (
    {
      value,
      onChange,
      onSubmit,
      onStop,
      isLoading,
      placeholder = "Ask a question...",
      className,
      hasMessages = false,
    },
    ref,
  ) => {
    const editorRef = useRef<ContentAreaRef>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    useImperativeHandle(ref, () => ({
      focus: () => editorRef.current?.focus(),
      getValue: () => value,
      setValue: (nextValue: string) => {
        editorRef.current?.editor?.commands.setContent(nextValue, {
          emitUpdate: true,
          parseOptions: { preserveWhitespace: "full" },
        });
        editorRef.current?.focus();
      },
    }));

    useEffect(() => {
      if (isLoading || !editorRef.current?.editor) {
        return;
      }
      if (chatStoreInstance.editingQueuedMessageId) {
        return;
      }

      editorRef.current.editor.setEditable(true);
      setTimeout(() => {
        if (!chatStoreInstance.editingQueuedMessageId) {
          editorRef.current?.focus();
        }
      }, 0);
    }, [isLoading]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
      },
      [onChange],
    );

    const handleSubmit = useCallback(
      (e?: React.FormEvent) => {
        e?.preventDefault();
        const message = value.trim();
        if (!message) {
          return;
        }

        editorRef.current?.editor?.commands.clearContent();
        onChange("");
        onSubmit(message);
      },
      [value, onChange, onSubmit],
    );

    const handleEditorKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter") {
          const tagMentionMenu = document.querySelector('[data-tag-mention-menu="true"]');
          if (tagMentionMenu) {
            return false;
          }

          if (e.metaKey || e.ctrlKey) {
            if (value.trim()) {
              e.preventDefault();
              handleSubmit();
              return true;
            }
            return false;
          }

          if (e.shiftKey) {
            e.preventDefault();
            editorRef.current?.editor?.commands.first(({ commands }) => [
              () => commands.newlineInCode(),
              () => commands.createParagraphNear(),
              () => commands.liftEmptyBlock(),
              () => commands.splitBlock(),
            ]);
            return true;
          }

          return false;
        }

        return false;
      },
      [value, handleSubmit],
    );

    const handleContainerFocus = useCallback(() => {
      setIsFocused(true);
    }, []);

    const handleContainerBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
        setIsFocused(false);
      }
    }, []);

    const canSend = value.trim().length > 0;
    const submitShortcutLabel = useMemo(() => {
      if (typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform)) {
        return "⌘ + ↵";
      }
      return "Ctrl + ↵";
    }, []);

    const sendTooltip = isLoading
      ? `Queue message (${submitShortcutLabel})`
      : `Send message (${submitShortcutLabel})`;

    return (
      <div
        ref={containerRef}
        onFocus={handleContainerFocus}
        onBlur={handleContainerBlur}
        className={cn(
          "relative w-full overflow-hidden rounded-[22px] border border-gray-200 bg-white",
          "transition-[border-color] duration-200 ease-out",
          "max-h-[60vh]",
          isFocused && "border-blue-500",
          hasMessages && "pb-0 pt-0",
          className,
        )}
      >
        <div className="flex w-full flex-col px-4 pt-3">
          <MessageQueue />
          <div className="min-w-0 flex-1">
            <ContentArea
              ref={editorRef}
              config={{
                disabledNodes: [ContentNodeType.Snippet, ContentNodeType.DatasetExamples],
              }}
              enableTagging
              allowEntityTypeTags
              allowUserTags={false}
              value={value}
              onChange={handleChange}
              onKeyDown={handleEditorKeyDown}
              placeholder={placeholder}
              hideContextMenu
              markdownEnabled={false}
              size="xs"
              className={cn(
                "max-h-[200px] min-h-[36px] w-full resize-none overflow-y-auto border-0 bg-transparent px-1 py-2 text-sm text-gray-900 shadow-none outline-none ring-0 placeholder:text-gray-500 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50",
                "[&_.ProseMirror]:min-h-[18px] [&_.ProseMirror]:whitespace-pre-wrap [&_.ProseMirror]:border-0 [&_.ProseMirror]:bg-transparent [&_.ProseMirror]:shadow-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:ring-0 [&_.ProseMirror]:focus:outline-none [&_.ProseMirror]:focus:ring-0 [&_.ProseMirror_p.is-editor-empty]:min-h-[18px] [&_p]:m-0",
              )}
            />
          </div>
          <div className="flex items-center justify-between px-0 pb-3 pt-1.5">
            <span className="select-none pl-1 text-[11px] tracking-wide text-zinc-300">
              {isLoading ? `${submitShortcutLabel} to queue` : `${submitShortcutLabel} to send`}
            </span>
            <div className="flex items-center gap-2">
              {isLoading && onStop ? (
                <BasicTooltip content="Stop generating">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onStop}
                    className="h-9 w-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700"
                  >
                    <SquareIcon className="h-4 w-4 fill-current" />
                  </Button>
                </BasicTooltip>
              ) : null}
              <BasicTooltip content={sendTooltip}>
                <Button
                  onClick={handleSubmit}
                  disabled={!canSend}
                  size="icon"
                  className="h-9 w-9 rounded-full"
                >
                  <CornerDownLeftIcon className="h-4 w-4" />
                </Button>
              </BasicTooltip>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

ChatInput.displayName = "ChatInput";
